import clsx from 'clsx'
import {
  getPublicLoginPath,
  isAuthGradientDomain,
  isSelectiveAiDomains,
} from 'common/HelperFunctions'
import CustomField from 'common/components/CustomField'
import Spinner from 'common/components/Spinner'
import {calculateErrorMessage} from 'common/helpers'
import {Form, Formik, FormikValues} from 'formik'
import {FC, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {Link, Navigate, useLocation, useNavigate, useSearchParams} from 'react-router'
import {toast} from 'react-toastify'
import {resetPassword} from 'setup/redux/Actions/SignIn/forgotPassword'
import * as Yup from 'yup'

import {systemMode, useThemeMode} from '_metronic/partials'

import {clearResetSession, readResetSession} from '../selective/forgotSetPasswordStorage'
import {clearMustCreatePassword} from '../selective/mustCreatePasswordHelpers'
import {handlePasswordResetComplete} from '../selective/passwordResetHelpers'
import {EmailPurpose, PasswordResetCompleteResult} from '../selective/types'

const MIN_LENGTH = 8
const MAX_LENGTH = 20

const Requirement: FC<{label: string; met: boolean}> = ({label, met}) => (
  <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
    <i
      className={met ? 'fas fa-check' : 'fas fa-times'}
      style={{fontSize: 11, color: met ? '#22c55e' : '#ef4444', width: 14, flexShrink: 0}}
    />
    <span style={{fontSize: 12, color: met ? '#22c55e' : '#ef4444'}}>{label}</span>
  </div>
)

const basePasswordSchema = Yup.string().label('Password').required()
const selectivePasswordSchema = basePasswordSchema
  .min(MIN_LENGTH, `Password must be at least ${MIN_LENGTH} characters`)
  .max(MAX_LENGTH, `Password must be at most ${MAX_LENGTH} characters`)
  .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .matches(/\d/, 'Password must contain at least one number')
  .matches(/[^A-Za-z0-9]/, 'Password must contain at least one special character')

const getResetPasswordSchema = (isSelective: boolean, requireCodeField: boolean) =>
  Yup.object().shape({
    code: requireCodeField ? Yup.string().required() : Yup.string(),
    password: isSelective ? selectivePasswordSchema : basePasswordSchema,
    password_confirmation: Yup.string()
      .oneOf([Yup.ref('password')], 'Password must match')
      .label('Confirm Password')
      .required(),
  })

export function ResetPassword() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const isSelectiveAiDomain = isSelectiveAiDomains()
  const dispatch = useDispatch()
  const {mode: themeMode} = useThemeMode()
  // The gradient/selective auth layouts render the form on a dark card, so the
  // default dark title/label colours would be invisible — mirror Login's rule.
  const isSpecialDomain = isAuthGradientDomain() || isSelectiveAiDomain
  const onDarkBg =
    isSpecialDomain &&
    (isAuthGradientDomain() || (themeMode === 'system' ? systemMode : themeMode) === 'dark')
  const titleClass = onDarkBg ? 'text-white' : 'text-dark'
  const subtitleClass = onDarkBg ? 'text-white-50' : 'text-gray-400'
  const labelClass = clsx('fw-bolder fs-6', titleClass)

  const session = readResetSession()
  const state = location.state as {
    resetSessionToken?: string
    emailPurpose?: EmailPurpose
    email?: string
    username?: string
  } | null

  const resetSessionToken = isSelectiveAiDomain
    ? (state?.resetSessionToken ?? session.resetSessionToken)
    : null

  const emailPurpose = isSelectiveAiDomain ? (state?.emailPurpose ?? session.emailPurpose) : null

  const accountEmail = isSelectiveAiDomain ? (state?.email ?? session.email) : null

  const legacyCode = !isSelectiveAiDomain ? searchParams.get('code') : null
  const code = isSelectiveAiDomain ? resetSessionToken : legacyCode

  const requireCodeField = !isSelectiveAiDomain && !legacyCode

  const initialValues = useMemo(
    () => ({
      code: code ?? '',
      password: '',
      password_confirmation: '',
    }),
    [code]
  )

  if (isSelectiveAiDomain && !code) {
    return <Navigate to='/auth/forgot-password' replace />
  }

  const pageTitle =
    isSelectiveAiDomain && emailPurpose === 'set' ? 'Set Your Password' : 'Reset Password'
  const pageSubtitle =
    isSelectiveAiDomain && emailPurpose === 'set'
      ? 'Create a password to access your account'
      : 'Enter new password'

  const handleSubmit = (values: typeof initialValues, actions: FormikValues) => {
    setHasErrors(undefined)

    const onSuccess = (res: {result?: PasswordResetCompleteResult}) => {
      if (isSelectiveAiDomain && handlePasswordResetComplete(res)) {
        return
      }

      actions.setSubmitting(false)
      if (isSelectiveAiDomain) {
        clearResetSession()
        clearMustCreatePassword(dispatch)
      }
      toast.success(
        emailPurpose === 'set' ? 'Password set successfully' : 'Password reset successfully'
      )
      navigate(isSelectiveAiDomain ? getPublicLoginPath() : '/auth/login')
    }

    const onFailure = (err: {message?: string; statusCode?: number}) => {
      actions.setSubmitting(false)
      toast.error(calculateErrorMessage(err, 'Error requesting password reset'))
    }

    const body = {
      code: values.code || code,
      password: values.password,
      retypePassword: values.password_confirmation,
    }

    dispatch(resetPassword(body, onSuccess, onFailure))
  }

  const handleCancel = () => {
    if (isSelectiveAiDomain) {
      clearResetSession()
    }
    navigate(getPublicLoginPath())
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getResetPasswordSchema(isSelectiveAiDomain, requireCodeField)}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {(formik) => (
        <Form
          className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
          noValidate
          id='kt_login_password_reset_form'
          autoComplete={isSelectiveAiDomain ? 'on' : undefined}
        >
          <div className='text-center mb-10'>
            <h1 className={clsx('fw-bolder mb-3', titleClass)}>{pageTitle}</h1>
            <div className={clsx('fw-bold fs-4', subtitleClass)}>{pageSubtitle}</div>
          </div>

          {hasErrors === true && (
            <div className='mb-8 alert alert-danger'>
              <div className='alert-text font-weight-bold'>
                Sorry, looks like there are some errors detected, please try again.
              </div>
            </div>
          )}

          {hasErrors === false && (
            <div className='mb-10 bg-light-info p-8 rounded'>
              <div className='text-info'>Password reset successfully.</div>
            </div>
          )}

          {requireCodeField && (
            <CustomField
              name='code'
              label='Code'
              labelClassName={labelClass}
              required={isSelectiveAiDomain}
            />
          )}

          {/* HTML-spec pattern for password-reset forms: a type="hidden"
              autocomplete="username" input anchors the saved credential to
              this account, so Safari's "Save Password?" sheet prefills the
              user name. type="hidden" specifically — the earlier off-screen
              CSS-hidden text input was ignored by Safari. */}
          {isSelectiveAiDomain && accountEmail && (
            <input type='hidden' name='username' autoComplete='username' value={accountEmail} />
          )}

          <CustomField
            name='password'
            label='Password'
            labelClassName={labelClass}
            type='password'
            required={isSelectiveAiDomain}
            autoComplete='new-password'
          />

          {isSelectiveAiDomain &&
            formik.values.password &&
            (() => {
              const pw = formik.values.password
              const hasMinLen = pw.length >= MIN_LENGTH
              const hasMaxLen = pw.length <= MAX_LENGTH
              const hasUpper = /[A-Z]/.test(pw)
              const hasNum = /\d/.test(pw)
              const hasSym = /[^A-Za-z0-9]/.test(pw)
              const hasValidLen = hasMinLen && hasMaxLen
              const metCount = [hasValidLen, hasUpper, hasNum, hasSym].filter(Boolean).length
              const pct = (metCount / 4) * 100
              const color = metCount <= 1 ? '#ef4444' : metCount <= 3 ? '#f59e0b' : '#22c55e'

              return (
                <div style={{marginBottom: 12}}>
                  <div
                    style={{
                      height: 4,
                      borderRadius: 4,
                      background: onDarkBg ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.08)',
                      overflow: 'hidden',
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${pct}%`,
                        background: color,
                        borderRadius: 4,
                        transition: 'width 0.3s ease, background 0.3s ease',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      color: onDarkBg ? 'rgba(255,255,255,0.75)' : '#6b7280',
                      fontSize: 12,
                      marginBottom: 6,
                    }}
                  >
                    Password must include:
                  </div>
                  <Requirement label={`${MIN_LENGTH}-${MAX_LENGTH} characters`} met={hasValidLen} />
                  <Requirement label='At least one capital letter' met={hasUpper} />
                  <Requirement label='At least one number' met={hasNum} />
                  <Requirement label='At least one special character' met={hasSym} />
                </div>
              )
            })()}

          <CustomField
            name='password_confirmation'
            label='Confirm Password'
            labelClassName={labelClass}
            type='password'
            required={isSelectiveAiDomain}
            autoComplete='new-password'
          />

          {isSelectiveAiDomain ? (
            <div className='text-center'>
              <button
                type='submit'
                id='kt_password_reset_submit'
                className='btn btn-lg btn-primary fw-bolder w-100 mb-5 rounded'
                disabled={
                  formik.isSubmitting ||
                  !formik.isValid ||
                  !formik.values.password ||
                  !formik.values.password_confirmation
                }
              >
                {formik.isSubmitting && <Spinner size='sm' type='grow' className='me-1' />}
                <span className='indicator-label'>Submit</span>
              </button>
              <button
                type='button'
                id='kt_login_password_reset_form_cancel_button'
                className='btn btn-light w-100 rounded'
                disabled={formik.isSubmitting}
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
              <button
                type='submit'
                id='kt_password_reset_submit'
                className='btn btn-lg btn-primary fw-bolder me-4'
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting && <Spinner size='sm' type='grow' className='me-1' />}
                <span className='indicator-label'>Submit</span>
              </button>
              <Link to='/auth/login'>
                <button
                  type='button'
                  id='kt_login_password_reset_form_cancel_button'
                  className='btn btn-light'
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  Cancel
                </button>
              </Link>
            </div>
          )}
        </Form>
      )}
    </Formik>
  )
}
